import React, { useState } from 'react';
import { generatePdf, downloadPdf } from '../utils/pdfGenerator';
import { useToast } from './ui/Toast';

export default function OutputOptions({ record, onShareCreated }) {
  const toast = useToast();
  const [pdfState, setPdfState] = useState('idle');
  const [shareState, setShareState] = useState('idle');
  const [shareUrl, setShareUrl] = useState(null);
  const [expertState, setExpertState] = useState('idle');
  const [printState, setPrintState] = useState('idle');

  const handleExpertRequest = async () => {
    if (!record?.record_id) {
      toast.error('기록 ID가 없습니다.');
      return;
    }
    setExpertState('loading');
    try {
      const response = await fetch('/api/snap/expert/request', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          record_id: record.record_id,
          scenario_type: record.scenario_type,
          message: '',
        }),
      });
      const result = await response.json();
      if (result.success) {
        toast.success('전문가 확인 요청이 접수되었습니다.');
        setExpertState('done');
        setTimeout(() => setExpertState('idle'), 3000);
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Expert request error:', error);
      toast.error('전문가 요청 실패: ' + error.message);
      setExpertState('idle');
    }
  };

  const handlePrint = async () => {
    setPrintState('loading');
    try {
      const blob = await generatePdf(record);
      const url = URL.createObjectURL(blob);
      const printWindow = window.open(url);
      if (printWindow) {
        printWindow.onload = () => printWindow.print();
      }
      setPrintState('done');
      toast.success('인쇄 준비 완료');
      setTimeout(() => setPrintState('idle'), 2000);
    } catch (error) {
      console.error('Print error:', error);
      toast.error('인쇄 준비 실패: ' + error.message);
      setPrintState('idle');
    }
  };

  const handlePdf = async () => {
    setPdfState('loading');
    try {
      const blob = await generatePdf(record);
      const filename = `snaplog-${record.scenario_type || 'report'}-${new Date().toISOString().slice(0, 10)}.pdf`;
      downloadPdf(blob, filename);
      setPdfState('done');
      toast.success('PDF가 다운로드되었습니다.');
      setTimeout(() => setPdfState('idle'), 2000);
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('PDF 생성에 실패했습니다: ' + error.message);
      setPdfState('idle');
    }
  };

  const handleShare = async () => {
    if (!record?.record_id) {
      toast.error('기록 ID가 없습니다.');
      return;
    }
    setShareState('loading');
    try {
      const response = await fetch('/api/snap/report/share', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ record_id: record.record_id }),
      });
      const result = await response.json();
      if (result.success) {
        const url = window.location.origin + result.data.shareUrl;
        setShareUrl(url);
        if (onShareCreated) onShareCreated(url);

        try {
          await navigator.clipboard.writeText(url);
          toast.success('공유 링크가 클립보드에 복사되었습니다!');
        } catch {
          toast.info('공유 링크가 생성되었습니다.');
        }
        setShareState('done');
        setTimeout(() => setShareState('idle'), 2000);
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Share error:', error);
      toast.error('공유 링크 생성 실패: ' + error.message);
      setShareState('idle');
    }
  };

  const getButtonContent = (state, loadingText, idleIcon, idleText) => {
    if (state === 'loading') {
      return (
        <>
          <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }}>⏳</span>
          {' '}{loadingText}
        </>
      );
    }
    if (state === 'done') {
      return (
        <>
          <span style={{ animation: 'scaleIn 0.3s ease' }}>✓</span>
          {' '}완료
        </>
      );
    }
    return `${idleIcon} ${idleText}`;
  };

  return (
    <div style={{
      display: 'flex',
      gap: '0.75rem',
      flexDirection: 'row',
      flexWrap: 'wrap',
    }}>
      <button
        onClick={handlePdf}
        disabled={pdfState === 'loading'}
        style={{
          flex: '1 1 120px',
          padding: '0.75rem',
          background: pdfState === 'loading'
            ? '#ccc'
            : pdfState === 'done'
              ? 'var(--color-success, #28a745)'
              : 'var(--color-accent, #667eea)',
          color: 'white',
          border: 'none',
          borderRadius: 'var(--radius-md, 0.5rem)',
          cursor: pdfState === 'loading' ? 'not-allowed' : 'pointer',
          fontSize: 'var(--font-size-sm, 0.9rem)',
          fontWeight: 700,
          transition: 'background var(--transition-fast, 150ms)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.25rem',
        }}
      >
        {getButtonContent(pdfState, 'PDF 생성 중...', '📄', 'PDF 다운로드')}
      </button>

      <button
        onClick={handleShare}
        disabled={shareState === 'loading'}
        style={{
          flex: '1 1 120px',
          padding: '0.75rem',
          background: shareState === 'loading'
            ? '#ccc'
            : shareState === 'done'
              ? 'var(--color-success, #28a745)'
              : 'var(--color-success, #28a745)',
          color: 'white',
          border: 'none',
          borderRadius: 'var(--radius-md, 0.5rem)',
          cursor: shareState === 'loading' ? 'not-allowed' : 'pointer',
          fontSize: 'var(--font-size-sm, 0.9rem)',
          fontWeight: 700,
          transition: 'background var(--transition-fast, 150ms)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.25rem',
        }}
      >
        {getButtonContent(shareState, '링크 생성 중...', '🔗', '공유 링크')}
      </button>

      {/* 인쇄 버튼 */}
      <button
        onClick={handlePrint}
        disabled={printState === 'loading'}
        style={{
          flex: '1 1 120px',
          padding: '0.75rem',
          background: printState === 'loading' ? '#ccc' : printState === 'done' ? 'var(--color-success, #28a745)' : '#6c757d',
          color: 'white',
          border: 'none',
          borderRadius: 'var(--radius-md, 0.5rem)',
          cursor: printState === 'loading' ? 'not-allowed' : 'pointer',
          fontSize: 'var(--font-size-sm, 0.9rem)',
          fontWeight: 700,
          transition: 'background var(--transition-fast, 150ms)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.25rem',
        }}
      >
        {getButtonContent(printState, '준비 중...', '🖨️', '인쇄')}
      </button>

      {/* 전문가 확인 요청 */}
      <button
        onClick={handleExpertRequest}
        disabled={expertState === 'loading' || expertState === 'done'}
        style={{
          flex: '1 1 120px',
          padding: '0.75rem',
          background: expertState === 'loading' ? '#ccc' : expertState === 'done' ? 'var(--color-success, #28a745)' : '#e67e22',
          color: 'white',
          border: 'none',
          borderRadius: 'var(--radius-md, 0.5rem)',
          cursor: expertState === 'loading' || expertState === 'done' ? 'not-allowed' : 'pointer',
          fontSize: 'var(--font-size-sm, 0.9rem)',
          fontWeight: 700,
          transition: 'background var(--transition-fast, 150ms)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.25rem',
        }}
      >
        {getButtonContent(expertState, '요청 중...', '👨‍🔬', '전문가 확인')}
      </button>

      {shareUrl && (
        <div style={{
          width: '100%',
          padding: 'var(--space-sm, 0.5rem)',
          background: 'var(--color-surface-dim, #f8f9fa)',
          borderRadius: 'var(--radius-sm, 0.25rem)',
          fontSize: 'var(--font-size-xs, 0.8rem)',
          color: 'var(--color-text-secondary, #666)',
          wordBreak: 'break-all',
          animation: 'fadeInUp 0.3s ease forwards',
        }}>
          {shareUrl}
        </div>
      )}

      {/* 면책 고지 */}
      <div style={{
        width: '100%',
        padding: 'var(--space-xs, 0.25rem) var(--space-sm, 0.5rem)',
        fontSize: 'var(--font-size-xs, 0.75rem)',
        color: 'var(--color-text-muted, #999)',
        textAlign: 'center',
      }}>
        ※ AI 분석 결과는 참고용입니다. 중요한 판단은 전문가에게 문의하세요.
      </div>
    </div>
  );
}
